import { mat4, vec3 } from 'gl-matrix';
import { BoundingSphere } from '../util';
import { RenderedCubeMapTexture } from '../texture/RenderedCubeMapTexture';
import { Context } from '../context/Context';
import { DepthMapRenderer, DepthMode, SceneRenderer } from '../renderer';
import { Scene } from '../scene/Scene';
import { Camera } from '../camera';
import { toRadian } from '../math';
import { Light, LightCamera, LightType, SHADOW_MAP_DIMENSIONS, BaseLightProperties } from './Light';

export type PointLightProperties = BaseLightProperties & {
  innerRange: number;
  outerRange: number;
};

export class PointLight extends Light {
  public readonly type: LightType.Point = LightType.Point;

  public innerRange: number;
  public outerRange: number;
  declare public boundingSphere: BoundingSphere;

  declare public camera: PointLightCamera;
  private depthRenderer?: DepthMapRenderer;

  constructor(properties: PointLightProperties) {
    super(properties);
    this.innerRange = properties.innerRange;
    this.outerRange = properties.outerRange;
    this.boundingSphere = new BoundingSphere(this.position, this.outerRange);
    this.camera = new PointLightCamera(SHADOW_MAP_DIMENSIONS, this);
    this.camera.setup();
  }

  public updateBounds(): void {
    this.boundingSphere.position = this.position;
    this.markDirty();
  }

  public renderShadowMap(ctx: Context): void {
    const scene = ctx.engine.activeScene!;
    const im = scene.instanceManager;

    if (!this.depthRenderer) {
      this.depthRenderer = new DepthMapRenderer(DepthMode.PointDistance, this.position);
      this.depthRenderer.init(ctx);
    }
    // Re-bind depthPoint to the current light position. `light.position` is
    // reassigned (not mutated in place) when the editor moves a light, so the
    // reference captured in the constructor goes stale. The depth shader uses
    // this to write linear distance-to-light into the cube map; if it points
    // to the old array, the cube's stored distances are from the old light
    // pose while the lighting pass samples with the new pose — the cube only
    // looks correct when the light is at its original position.
    this.depthRenderer.depthPoint = this.position;
    this.depthRenderer.instanceManager = im;
    this.renderCube(ctx, this.depthRenderer, scene);
  }

  private renderCube(ctx: Context, renderer: SceneRenderer, scene: Scene) {
    if (!this.shadowMap) {
      const cube = new RenderedCubeMapTexture(
        (camera, side, glCubeMapSide) => this.setCubeFaceCamera(camera, side, glCubeMapSide),
        true,
        SHADOW_MAP_DIMENSIONS,
      );
      cube.init(ctx);
      this.shadowMap = cube;
    }

    this.shadowMap.renderTexture(renderer, scene, this.camera);
  }

  private setCubeFaceCamera(camera: Camera, side: string, _glCubeMapSide: number): Camera {
    switch (side) {
      case 'posx':
        this.camera.setViewAxis(0, 1, [0, -1, 0]);
        break;
      case 'negx':
        this.camera.setViewAxis(0, -1, [0, -1, 0]);
        break;
      case 'posy':
        this.camera.setViewAxis(1, 1, [0, 0, 1]);
        break;
      case 'negy':
        this.camera.setViewAxis(1, -1, [0, 0, -1]);
        break;
      case 'posz':
        this.camera.setViewAxis(2, 1, [0, -1, 0]);
        break;
      case 'negz':
        this.camera.setViewAxis(2, -1, [0, -1, 0]);
        break;
    }
    return camera;
  }

  public packGpuData(data: Float32Array, o: number): void {
    this.packCommon(data, o);
    data[o + 7] = this.outerRange * this.outerRange; // outerRange² for fast distance check

    // Texel 2: direction (unused for point lights)
    data[o + 8] = 0;
    data[o + 9] = 0;
    data[o + 10] = 0;
    data[o + 11] = 0;

    // Texel 3: ranges (cone limits cos(180°) = -1)
    data[o + 12] = this.innerRange;
    data[o + 13] = this.outerRange;
    data[o + 14] = -1;
    data[o + 15] = -1;
  }
}

export class PointLightCamera extends LightCamera<PointLight> {
  private viewAxis: number = 1;
  private viewAxisDirection: number = -1;

  constructor(dimensions: { width: number; height: number }, light: PointLight) {
    super(dimensions, light);
    this.up = vec3.fromValues(0, 0, -1);
  }

  public setViewAxis(axis: number, direction: number, up: vec3 | number[]): void {
    this.viewAxis = axis;
    this.viewAxisDirection = direction;
    this.up = up;
    this.setup();
  }

  protected determineTargetPosition(): vec3 {
    const target = vec3.create();
    vec3.copy(target, this.light.position as vec3);
    target[this.viewAxis] += this.viewAxisDirection;
    return target;
  }

  protected determineUp(): vec3 | number[] {
    return this.up;
  }

  protected determineProjection(): mat4 {
    const matrix = mat4.create();
    mat4.perspective(matrix, toRadian(90), this.aspectRatio, 0.1, this.light.outerRange);
    return matrix;
  }
}
